import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { uk } from "date-fns/locale";
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  Clock,
  FileText,
  Lock,
  RotateCcw,
  Sparkles,
  Undo2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import {
  fetchMeetingsQueue,
  markMeetingOccurred,
  markMeetingOccurredOnBehalf,
  type MeetingsQueueFilter,
  type MeetingsQueueItem,
  type MeetingsQueueScope,
} from "@/api/meetingsQueue";
import { OnBehalfConfirmModal, type OnBehalfProceedContext } from "@/components/meetings/OnBehalfConfirmModal";
import { ReportRequiredBanner } from "@/components/meetings/ReportRequiredBanner";
import { SupervisorControlPanel } from "@/components/meetings/SupervisorControlPanel";
import { MeetingDetailDrawer } from "@/components/meetings/MeetingDetailDrawer";
import { ApiClientError } from "@/api/client";
import { ClientPickerDrawer } from "@/components/home/ClientPickerDrawer";
import { ScheduleMeetingDrawer } from "@/components/client360/ScheduleMeetingDrawer";
import { ProspectPickerDrawer } from "@/components/meetings/ProspectPickerDrawer";
import { ScheduleProspectMeetingDrawer } from "@/components/meetings/ScheduleProspectMeetingDrawer";
import { MeetingReportDrawer, type ReportTarget } from "@/components/client360/MeetingReportDrawer";
import { MeetingTargetModeDrawer } from "@/components/meetings/MeetingTargetModeDrawer";

/**
 * D-055 S4 — Meetings Working Queue (visual v0.2 екран 1).
 *
 * Top-level «Зустрічі» (peer Задач/Запитів): 6 вкладок, backend-owned counts
 * (frontend НЕ інферить), scope=own|team (resolver). Рядок 72px (DS), клік
 * «Заповнити звіт» → канонічний C+ wizard (S2/S3). «Проведено» → mark-occurred
 * → рядок мігрує у «Звіт очікується» (доктрина: без звіту = незавершена
 * робота). Deep-links: ?report={id} (F6/D-043) · ?filter={tab}.
 */

const TABS: Array<{ k: MeetingsQueueFilter; label: string; danger?: boolean }> = [
  { k: "today", label: "Сьогодні" },
  { k: "planned", label: "Заплановані" },
  { k: "report_required", label: "Звіт очікується" },
  { k: "overdue", label: "Прострочені звіти", danger: true },
  { k: "conducted", label: "Проведені" },
  { k: "cancelled", label: "Скасовані / перенесені" },
];

const SCOPE_LABELS: Record<MeetingsQueueScope, string> = {
  own: "Мої",
  team: "Команда",
};

const TYPE_LABELS: Record<string, string> = {
  first_meeting: "перша зустріч",
  follow_up: "follow-up",
  payment_negotiation: "оплата",
  brand_presentation: "презентація",
  contract_negotiation: "переговори",
  problem_issue: "проблема",
  routine_visit: "візит",
  other: "інше",
};

function useMeetingsQueue(scope: MeetingsQueueScope, filter: MeetingsQueueFilter) {
  return useQuery({
    queryKey: ["meetingsQueue", scope, filter],
    queryFn: ({ signal }) => fetchMeetingsQueue({ scope, filter }, signal),
    staleTime: 30_000,
  });
}

export function MeetingsQueuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();

  const [tab, setTab] = useState<MeetingsQueueFilter>("today");
  // S5: «Контроль команди» — role-gated секція всередині /meetings (екран 5)
  const [section, setSection] = useState<"queue" | "control">("queue");
  const [scope, setScope] = useState<MeetingsQueueScope>("own");
  const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null);
  const [reportHoldingId, setReportHoldingId] = useState<string>("");
  // D-151 S2 — on-behalf: confirm-крок → C+-візард у supervisor-режимі
  const [onBehalfConfirmId, setOnBehalfConfirmId] = useState<string | null>(null);
  const [onBehalfCtx, setOnBehalfCtx] = useState<OnBehalfProceedContext | null>(null);
  const [occOnBehalf, setOccOnBehalf] = useState<{ id: string; revision: number; manager: string | null } | null>(null);
  // S6: «Відкрити» → MeetingDetailDrawer (Igor Q2; «Картка клієнта» всередині)
  const [detailId, setDetailId] = useState<string | null>(null);
  const [createStep, setCreateStep] = useState<
    | { kind: "mode" }                                          // D-134 W1 — existing/prospect
    | { kind: "pick" }                                          // existing holding-picker
    | { kind: "prospect-pick" }                                // D-134 W2/W3 — lead search/create
    | { kind: "schedule"; holdingId: string }
    | { kind: "schedule-prospect"; accountId: string; name: string } // D-134 W4
    | null
  >(null);
  /** F6/D-043 deep-link ?report={id} — чекає item у завантажених вкладках. */
  const pendingReportId = useRef<string | null>(null);
  const [pendingTried, setPendingTried] = useState<MeetingsQueueFilter[]>([]);

  const q = useMeetingsQueue(scope, tab);
  const allowedScopes = q.data?.allowedScopes ?? ["own"];
  const counts = q.data?.counts;
  const controlAllowed = q.data?.controlAllowed ?? false;

  // ── deep-links: ?report={id} → wizard · ?filter={tab}
  //    · ?scope=team (Supervisor Home C2 → team scope; backend tier-revalidates,
  //      manager→own clamp via allowedScopes so no broaden) ──
  useEffect(() => {
    const report = searchParams.get("report");
    const openDetail = searchParams.get("open");
    const filter = searchParams.get("filter") as MeetingsQueueFilter | null;
    const scopeParam = searchParams.get("scope");
    if (!report && !openDetail && !filter && !scopeParam) return;
    if (scopeParam === "team") setScope("team");
    if (filter && TABS.some((t) => t.k === filter)) setTab(filter);
    if (report) {
      pendingReportId.current = report;
      setTab(filter && TABS.some((t) => t.k === filter) ? filter : "report_required");
    }
    // D-137 W5a: ?open={id} → read drawer (MeetingDetailDrawer) directly. Used by
    // supervisor/manager notification «Відкрити» — the backend read-rescue grants
    // supervisor read; drawer fetches by id so it is independent of the loaded
    // queue tab/scope (no own-scope miss, no wrong fill-form for a read-only viewer).
    if (openDetail) setDetailId(openDetail);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // pending ?report= — шукаємо item у завантаженій вкладці; якщо немає —
  // пробуємо overdue (звіт міг прострочитись між сповіщенням і кліком).
  useEffect(() => {
    const id = pendingReportId.current;
    if (!id || !q.data) return;
    const item = q.data.items.find((m) => m.id === id);
    if (item) {
      pendingReportId.current = null;
      openReport(item);
      return;
    }
    if (!pendingTried.includes(tab)) {
      const next: MeetingsQueueFilter | null =
        tab === "report_required" ? "overdue"
        : tab === "overdue" ? "conducted"
        : null;
      setPendingTried((p) => [...p, tab]);
      if (next) {
        setTab(next);
      } else {
        pendingReportId.current = null; // не знайдено — тихо скидаємо
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data, tab]);

  const invalidateQueue = () => {
    void qc.invalidateQueries({ queryKey: ["meetingsQueue"] });
    void qc.invalidateQueries({ queryKey: ["homeSummary"] });
  };

  const occurredMut = useMutation({
    mutationFn: (m: { id: string; revision: number }) => markMeetingOccurred(m.id, m.revision),
    onSuccess: invalidateQueue,
  });

  const openReport = (m: MeetingsQueueItem) => {
    setReportHoldingId(m.targetHoldingId ?? "");
    setReportTarget({ interactionId: m.id, summary: m.summary });
  };

  // Банер (екран 2): найстаріша report-required зустріч активної вкладки.
  const bannerItem = useMemo(() => {
    if (!q.data) return null;
    if (!["today", "report_required", "overdue"].includes(tab)) return null;
    return q.data.items.find((m) => m.reportRequired) ?? null;
  }, [q.data, tab]);

  return (
    <>
      <div className="p-4 md:p-6">
        <div className="mx-auto w-full max-w-[1040px] space-y-5">
          {/* Header */}
          <div className="flex items-end justify-between flex-wrap gap-2">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Зустрічі та візити</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Зустріч → звіт → дії → контроль · проведена без звіту = незавершена робота
              </p>
            </div>
            <button
              type="button"
              onClick={() => setCreateStep({ kind: "mode" })}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
            >
              + Запланувати зустріч
            </button>
          </div>

          {/* Scope pill (resolver allowedScopes) */}
          {section === "queue" && allowedScopes.length > 1 && (
            <div className="inline-flex rounded-lg border bg-card p-1">
              {allowedScopes.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                    scope === s
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {SCOPE_LABELS[s]}
                </button>
              ))}
            </div>
          )}

          {/* Tabs — 6 вкладок queue + role-gated «Контроль команди» (S5) */}
          <div className="flex flex-wrap items-center gap-2">
            {TABS.map((t) => {
              const c = counts?.[t.k];
              const active = section === "queue" && tab === t.k;
              return (
                <button
                  key={t.k}
                  type="button"
                  onClick={() => {
                    setSection("queue");
                    setTab(t.k);
                  }}
                  className={cn(
                    "rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors",
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : t.danger && (c ?? 0) > 0
                        ? "border-destructive/40 text-destructive hover:bg-destructive/5"
                        : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t.label}
                  {typeof c === "number" && c > 0 && (
                    <span className="ml-1.5 opacity-80">{c}</span>
                  )}
                </button>
              );
            })}
            {controlAllowed && (
              <button
                type="button"
                onClick={() => setSection("control")}
                className={cn(
                  "rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors",
                  section === "control"
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                Контроль команди
              </button>
            )}
          </div>

          {/* S5 — Supervisor Control (екран 5; backend 403-ить не-supervisor) */}
          {section === "control" && controlAllowed && <SupervisorControlPanel />}

          {/* Report Required банер (екран 2) */}
          {section === "queue" && bannerItem && (
            <ReportRequiredBanner
              summary={bannerItem.summary}
              accountName={bannerItem.accountName}
              occurredAt={bannerItem.occurredAt}
              slaDueAt={bannerItem.reportSlaDueAt}
              overdueSince={bannerItem.reportOverdueSince}
              canFill={bannerItem.allowedActions.canFillReport}
              onFill={() => openReport(bannerItem)}
            />
          )}

          {/* List */}
          {section === "queue" && (q.isPending ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-[72px]" />
              ))}
            </div>
          ) : q.isError ? (
            q.error instanceof ApiClientError && q.error.status === 403 ? (
              /* S6 екран 6: no_access ≠ generic error */
              <div className="rounded-lg border bg-muted/30 p-8 text-center">
                <Lock className="mx-auto mb-2 h-5 w-5 text-muted-foreground" aria-hidden />
                <p className="text-sm font-medium">Немає доступу</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Зустрічі поза вашою зоною відповідальності (ACL). Зверніться до керівника.
                </p>
              </div>
            ) : (
              <div className="rounded-lg border bg-destructive/5 p-6 text-center">
                <AlertCircle className="mx-auto mb-2 h-5 w-5 text-destructive" />
                <p className="text-sm text-destructive">
                  {(q.error as Error)?.message ?? "Не вдалося завантажити"}
                </p>
                <Button size="sm" variant="outline" className="mt-3 gap-1.5" onClick={() => void q.refetch()}>
                  <RotateCcw className="h-3.5 w-3.5" /> Спробувати ще
                </Button>
              </div>
            )
          ) : (q.data?.items.length ?? 0) === 0 ? (
            /* S6 екран 6: empty з ЖИВИМ CTA (0 мертвих placeholder, §0) */
            <div className="rounded-lg border bg-muted/30 p-8 text-center">
              <CalendarDays className="mx-auto mb-2 h-5 w-5 text-muted-foreground" aria-hidden />
              <p className="text-sm font-medium">
                {tab === "today" ? "Сьогодні зустрічей немає" : "У цій вкладці порожньо"}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Заплануйте першу зустріч або візит з клієнтом.
              </p>
              <button
                type="button"
                onClick={() => setCreateStep({ kind: "mode" })}
                className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
              >
                + Запланувати зустріч
              </button>
            </div>
          ) : (
            <div className="space-y-2.5">
              {q.data!.items.map((m) => (
                <MeetingRow
                  key={m.id}
                  m={m}
                  scope={scope}
                  onOpen={() => setDetailId(m.id)}
                  onFillReport={() => openReport(m)}
                  onMarkOccurred={() => occurredMut.mutate({ id: m.id, revision: m.interactionRevision })}
                  occurredPending={occurredMut.isPending && occurredMut.variables?.id === m.id}
                  onFillOnBehalf={() => setOnBehalfConfirmId(m.id)}
                  onMarkOccurredOnBehalf={() => setOccOnBehalf({ id: m.id, revision: m.interactionRevision, manager: m.assignedUserName })}
                />
              ))}
            </div>
          ))}

          {section === "queue" && (
            <p className="px-1 text-[11px] text-muted-foreground">
              Статуси: Заплановано · Проведено · Звіт очікується · Прострочено · Скасовано · Перенесено.
              Counts — backend-owned. Звіт і дії — канонічний C+ wizard (одна форма на CRM).
            </p>
          )}
        </div>
      </div>

      {/* D-134 W1 — «З ким зустріч?» (existing/prospect) */}
      {createStep?.kind === "mode" && (
        <MeetingTargetModeDrawer
          open
          onClose={() => setCreateStep(null)}
          onExisting={() => setCreateStep({ kind: "pick" })}
          onProspect={() => setCreateStep({ kind: "prospect-pick" })}
        />
      )}

      {/* «+ Запланувати» поза контекстом клієнта → picker → canonical drawer (D-031) */}
      {createStep?.kind === "pick" && (
        <ClientPickerDrawer
          open
          onClose={() => setCreateStep(null)}
          title="Зустріч — оберіть клієнта"
          hint="Зустріч планується в контексті клієнта (контакти підтягнуться з його картки)."
          onPick={(holdingId) => setCreateStep({ kind: "schedule", holdingId })}
        />
      )}
      {createStep?.kind === "schedule" && (
        <ScheduleMeetingDrawer
          open
          onClose={() => {
            setCreateStep(null);
            invalidateQueue();
          }}
          holdingId={createStep.holdingId}
        />
      )}

      {/* D-134 W2/W3 — prospect search/create → W4 account-centric meeting */}
      {createStep?.kind === "prospect-pick" && (
        <ProspectPickerDrawer
          open
          onClose={() => setCreateStep(null)}
          onPick={(accountId, name) => setCreateStep({ kind: "schedule-prospect", accountId, name })}
        />
      )}
      {createStep?.kind === "schedule-prospect" && (
        <ScheduleProspectMeetingDrawer
          open
          accountId={createStep.accountId}
          prospectName={createStep.name}
          onCreated={invalidateQueue}
          onClose={() => {
            setCreateStep(null);
            invalidateQueue();
          }}
        />
      )}

      {/* S6 — Meeting Detail Drawer (екран 3 CTA-баланс; «Відкрити» з черги) */}
      <MeetingDetailDrawer
        interactionId={detailId}
        onClose={() => setDetailId(null)}
        onChanged={invalidateQueue}
        onFillReport={(d) => {
          setDetailId(null);
          setReportHoldingId(d.targetHoldingId ?? "");
          setReportTarget({ interactionId: d.id, summary: d.summary });
        }}
        onFillOnBehalf={(d) => {
          setDetailId(null);
          setOnBehalfConfirmId(d.id);
        }}
        onMarkOccurredOnBehalf={(d) => {
          setDetailId(null);
          setOccOnBehalf({ id: d.id, revision: d.interactionRevision, manager: d.assignedUserName });
        }}
      />

      {/* Канонічний C+ wizard (S2/S3) — той самий drawer, що у Client360 */}
      <MeetingReportDrawer
        target={reportTarget}
        holdingId={reportHoldingId}
        onBehalfCtx={onBehalfCtx}
        onClose={() => {
          setReportTarget(null);
          setOnBehalfCtx(null);
          invalidateQueue();
        }}
      />

      {/* D-151 S2 — confirm-крок «Звіт за менеджера» (4 стани, P62) */}
      <OnBehalfConfirmModal
        interactionId={onBehalfConfirmId}
        onClose={() => setOnBehalfConfirmId(null)}
        onProceed={(d, ctx) => {
          setOnBehalfConfirmId(null);
          setOnBehalfCtx(ctx);
          setReportHoldingId(d.targetHoldingId ?? "");
          setReportTarget({ interactionId: d.id, summary: d.summary });
        }}
      />

      {/* D-151 S2 — reason-модалка «Проведено (за менеджера)» (Q-I1=B) */}
      <OccurredOnBehalfModal
        target={occOnBehalf}
        onClose={() => setOccOnBehalf(null)}
        onDone={() => {
          setOccOnBehalf(null);
          invalidateQueue();
        }}
      />
    </>
  );
}

// ── row (visual екран 1: 72px, strip-колір за станом) ──

function MeetingRow({
  m,
  scope,
  onOpen,
  onFillReport,
  onMarkOccurred,
  occurredPending,
  onFillOnBehalf,
  onMarkOccurredOnBehalf,
}: {
  m: MeetingsQueueItem;
  scope: MeetingsQueueScope;
  onOpen: () => void;
  onFillReport: () => void;
  onMarkOccurred: () => void;
  occurredPending: boolean;
  onFillOnBehalf: () => void;
  onMarkOccurredOnBehalf: () => void;
}) {
  const overdue = !!m.reportOverdueSince && !m.reportSubmitted;
  const required = m.reportRequired && !overdue;
  const cancelled = ["cancelled", "rescheduled"].includes(m.meetingStatus);
  const done = m.reportSubmitted || ["completed", "occurred_no_report_needed", "exception_no_report_required"].includes(m.meetingStatus);

  const strip = overdue
    ? "bg-destructive"
    : required
      ? "bg-warning-foreground"
      : cancelled
        ? "bg-muted-foreground/40"
        : done
          ? "bg-primary"
          : "bg-violet-foreground";

  const Icon = overdue ? Clock : required ? FileText : cancelled ? Undo2 : done ? CheckCircle2 : CalendarDays;

  const fmt = (v: string | null, pattern: string) => {
    if (!v) return null;
    try {
      return format(parseISO(v.replace(" ", "T")), pattern, { locale: uk });
    } catch {
      return null;
    }
  };

  const when = fmt(m.occurredAt, "dd.MM HH:mm");
  const typeLabel = m.meetingType ? TYPE_LABELS[m.meetingType] ?? m.meetingType : null;
  const overdueDays = m.reportOverdueSince
    ? Math.max(1, Math.floor((Date.now() - parseISO(m.reportOverdueSince.replace(" ", "T")).getTime()) / 86_400_000))
    : 0;

  return (
    <div className="flex min-h-[72px] items-center gap-3 rounded-xl border bg-card px-3.5 py-3">
      <span className={cn("w-[3px] self-stretch rounded-full", strip)} aria-hidden />
      <span
        className={cn(
          "grid h-10 w-10 shrink-0 place-items-center rounded-lg",
          overdue
            ? "bg-destructive/10 text-destructive"
            : required
              ? "bg-warning text-warning-foreground"
              : "bg-muted text-muted-foreground",
        )}
      >
        <Icon className="h-4.5 w-4.5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5 text-sm font-semibold">
          <span className="truncate">{m.summary}</span>
          {m.isProspect && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10.5px] font-semibold text-primary">
              <Sparkles className="h-3 w-3" aria-hidden /> Потенційний клієнт
            </span>
          )}
          {typeLabel && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
              {typeLabel}
            </span>
          )}
          {overdue && (
            <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10.5px] font-bold text-destructive">
              звіт прострочено · −{overdueDays} {overdueDays === 1 ? "день" : "дн."}
            </span>
          )}
          {required && (
            <span className="rounded-full bg-warning px-2 py-0.5 text-[10.5px] font-bold text-warning-foreground">
              звіт очікується · SLA 24 год
            </span>
          )}
          {m.reviewStatus === "returned" && (
            <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10.5px] font-bold text-destructive">
              повернуто на уточнення
            </span>
          )}
          {m.reportSubmissionMode === "supervisor_on_behalf" && (
            <span className="rounded-full bg-violet px-2 py-0.5 text-[10.5px] font-bold text-violet-foreground">
              заповнив керівник{m.reportSubmittedByName ? `: ${m.reportSubmittedByName}` : ""}
            </span>
          )}
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {[
            m.accountName,
            m.contactName,
            scope === "team" && m.assignedUserName ? `менеджер: ${m.assignedUserName}` : null,
            when,
            m.durationMinutes ? `${m.durationMinutes} хв` : null,
            !overdue && m.reportRequired && m.reportSlaDueAt
              ? `звіт до ${fmt(m.reportSlaDueAt, "dd.MM HH:mm")}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          onClick={onOpen}
          className="rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold text-foreground hover:bg-muted"
        >
          Відкрити
        </button>
        {m.allowedActions.canMarkOccurred && (
          <button
            type="button"
            onClick={onMarkOccurred}
            disabled={occurredPending}
            className="rounded-md bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {occurredPending ? "…" : "Проведено"}
          </button>
        )}
        {m.allowedActions.canFillReport && !m.allowedActions.canMarkOccurred && (
          <button
            type="button"
            onClick={onFillReport}
            className="rounded-md bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Заповнити звіт
          </button>
        )}
        {/* D-151 S2 — on-behalf CTAs (server-derived; стан 4 = disabled-вигляд у confirm) */}
        {m.allowedActions.canMarkOccurredOnBehalf && (
          <button
            type="button"
            onClick={onMarkOccurredOnBehalf}
            className="rounded-md border border-primary/40 px-2.5 py-1.5 text-xs font-semibold text-primary hover:bg-primary/5"
          >
            Проведено (за менеджера)
          </button>
        )}
        {(m.allowedActions.canFillReportOnBehalf || m.allowedActions.canFillReportOnBehalfBlockedByFreshDraft) && (
          <button
            type="button"
            onClick={onFillOnBehalf}
            className="rounded-md border border-primary/40 px-2.5 py-1.5 text-xs font-semibold text-primary hover:bg-primary/5"
          >
            Звіт за менеджера
          </button>
        )}
      </div>
    </div>
  );
}


/** D-151 S2 — reason-модалка «Проведено позначає керівник» (Q-I1=B, P58). */
function OccurredOnBehalfModal({
  target,
  onClose,
  onDone,
}: {
  target: { id: string; revision: number; manager: string | null } | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () =>
      markMeetingOccurredOnBehalf(target!.id, { reason: reason.trim(), interactionRevision: target!.revision }),
    onSuccess: () => {
      setReason("");
      setErr(null);
      onDone();
    },
    onError: (e) => setErr(e instanceof ApiClientError ? e.message : "Не вдалося виконати дію"),
  });
  useEffect(() => {
    if (!target) {
      setReason("");
      setErr(null);
    }
  }, [target]);
  if (!target) return null;
  const ok = reason.trim().length >= 10 && reason.trim().length <= 500;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} aria-hidden>
      <div className="w-full max-w-[440px] rounded-xl border bg-background p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3 className="mb-1 text-sm font-semibold">Позначити зустріч проведеною за менеджера</h3>
        <p className="mb-3 text-xs text-muted-foreground">
          {target.manager ? `Менеджер: ${target.manager}. ` : ""}Менеджер отримає сповіщення;
          звіт лишається неподаним — дисципліна звітності не змінюється.
        </p>
        <label className="mb-1 block text-xs font-semibold">
          Причина <span className="text-destructive">*</span>
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Мін. 10 символів (буде видима менеджеру та в аудиті)."
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
        {err && <p className="mt-1 text-xs text-destructive">{err}</p>}
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-border px-3 py-2 text-sm font-semibold hover:bg-muted">
            Скасувати
          </button>
          <button
            type="button"
            disabled={!ok || mut.isPending}
            onClick={() => mut.mutate()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-45"
          >
            {mut.isPending ? "…" : "Підтвердити «Проведено»"}
          </button>
        </div>
      </div>
    </div>
  );
}
